import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs'
import { map } from 'rxjs/operators'
import { Dictionary } from '../../shared/models/main/dictionary'
import { Pair } from '../../shared/models/main/pair'
import { Word } from '../../shared/models/main/word'
import { DictionaryService } from '../data/dictionary.service'
import { PairService } from '../data/pair.service'
import { WordService } from '../data/word.service'

export interface ApiResponse<T> {
  results: T[]
  count: number
  detail?: string
}

interface DictionaryState {
  dictionary: Dictionary | null
  words: Word[]
  pairs: Pair[]
  currentPage: {
    words: number
    pairs: number
  }
  totalItems: {
    words: number
    pairs: number
  }
  loading: {
    dictionary: boolean
    words: boolean
    pairs: boolean
  }
  error: {
    dictionary: string | null
    words: string | null
    pairs: string | null
  }
}

@Injectable({
  providedIn: 'root',
})
export class DictionaryStateService {
  private readonly ITEMS_PER_PAGE = 10

  private state: DictionaryState = {
    dictionary: null,
    words: [],
    pairs: [],
    currentPage: {
      words: 1,
      pairs: 1,
    },
    totalItems: {
      words: 0,
      pairs: 0,
    },
    loading: {
      dictionary: false,
      words: false,
      pairs: false,
    },
    error: {
      dictionary: null,
      words: null,
      pairs: null,
    },
  }

  private stateSubject = new BehaviorSubject<DictionaryState>(this.state)

  constructor(
    private wordService: WordService,
    private pairService: PairService,
    private dictionaryService: DictionaryService,
  ) {}

  // Selectors
  get dictionary$(): Observable<Dictionary | null> {
    return this.stateSubject.pipe(map(state => state.dictionary))
  }

  get words$(): Observable<Word[]> {
    return this.stateSubject.pipe(map(state => state.words))
  }

  get pairs$(): Observable<Pair[]> {
    return this.stateSubject.pipe(map(state => state.pairs))
  }

  get totalItems$(): Observable<{ words: number; pairs: number }> {
    return this.stateSubject.pipe(map(state => state.totalItems))
  }

  get dictionaryLoading$(): Observable<boolean> {
    return this.stateSubject.pipe(map(state => state.loading.dictionary))
  }

  get wordsLoading$(): Observable<boolean> {
    return this.stateSubject.pipe(map(state => state.loading.words))
  }

  get pairsLoading$(): Observable<boolean> {
    return this.stateSubject.pipe(map(state => state.loading.pairs))
  }

  get dictionaryError$(): Observable<string | null> {
    return this.stateSubject.pipe(map(state => state.error.dictionary))
  }

  get wordsError$(): Observable<string | null> {
    return this.stateSubject.pipe(map(state => state.error.words))
  }

  get pairsError$(): Observable<string | null> {
    return this.stateSubject.pipe(map(state => state.error.pairs))
  }

  // Get current state values synchronously
  get currentDictionary(): Dictionary | null {
    return this.state.dictionary
  }

  get currentWords(): Word[] {
    return this.state.words
  }

  get currentPairs(): Pair[] {
    return this.state.pairs
  }

  // Actions
  async loadDictionary(dicId: number, ownerUsername: string): Promise<void> {
    this.updateState({
      ...this.state,
      loading: { ...this.state.loading, dictionary: true },
      error: { ...this.state.error, dictionary: null },
    })

    try {
      const response = (await firstValueFrom(
        this.dictionaryService.get_user_dic(dicId, ownerUsername),
      )) as any

      if (response && !response.detail) {
        this.updateState({
          ...this.state,
          dictionary: response as Dictionary,
          totalItems: {
            words: response.words_count || 0,
            pairs: response.pairs_count || 0,
          },
          loading: { ...this.state.loading, dictionary: false },
        })
      } else {
        throw new Error(response?.detail || 'Dictionary not found')
      }
    } catch (error) {
      this.updateState({
        ...this.state,
        loading: { ...this.state.loading, dictionary: false },
        error: {
          ...this.state.error,
          dictionary: error instanceof Error ? error.message : 'Failed to load dictionary',
        },
      })
      throw error
    }

    this.loadWords(true)
    this.loadPairs(true)
  }

  async loadWords(reset: boolean = false): Promise<void> {
    if (!this.state.dictionary) return

    if (reset) {
      this.updateState({
        ...this.state,
        currentPage: { ...this.state.currentPage, words: 1 },
        words: [],
      })
    }

    this.updateState({
      ...this.state,
      loading: { ...this.state.loading, words: true },
      error: { ...this.state.error, words: null },
    })

    try {
      const response = (await firstValueFrom(
        this.wordService.get_dic_words_page(
          this.state.dictionary.id,
          this.state.currentPage.words,
          this.ITEMS_PER_PAGE,
        ),
      )) as ApiResponse<Word>

      if (response && response.results) {
        this.updateState({
          ...this.state,
          words: reset ? response.results : [...this.state.words, ...response.results],
          totalItems: { ...this.state.totalItems, words: response.count || 0 },
          loading: { ...this.state.loading, words: false },
        })
      } else {
        this.updateState({
          ...this.state,
          loading: { ...this.state.loading, words: false },
          error: { ...this.state.error, words: 'No words found' },
        })
      }
    } catch (error) {
      this.updateState({
        ...this.state,
        loading: { ...this.state.loading, words: false },
        error: {
          ...this.state.error,
          words: error instanceof Error ? error.message : 'Failed to load words',
        },
      })
      throw error
    }
  }

  async loadPairs(reset: boolean = false): Promise<void> {
    if (!this.state.dictionary) return

    if (reset) {
      this.updateState({
        ...this.state,
        currentPage: { ...this.state.currentPage, pairs: 1 },
        pairs: [],
      })
    }

    this.updateState({
      ...this.state,
      loading: { ...this.state.loading, pairs: true },
      error: { ...this.state.error, pairs: null },
    })

    try {
      const response = (await firstValueFrom(
        this.pairService.get_dic_pairs_page(
          this.state.dictionary.id,
          this.state.currentPage.pairs,
          this.ITEMS_PER_PAGE,
        ),
      )) as ApiResponse<Pair>

      if (response && response.results) {
        this.updateState({
          ...this.state,
          pairs: reset ? response.results : [...this.state.pairs, ...response.results],
          totalItems: { ...this.state.totalItems, pairs: response.count || 0 },
          loading: { ...this.state.loading, pairs: false },
        })
      } else {
        this.updateState({
          ...this.state,
          loading: { ...this.state.loading, pairs: false },
          error: { ...this.state.error, pairs: 'No pairs found' },
        })
      }
    } catch (error) {
      this.updateState({
        ...this.state,
        loading: { ...this.state.loading, pairs: false },
        error: {
          ...this.state.error,
          pairs: error instanceof Error ? error.message : 'Failed to load pairs',
        },
      })
      throw error
    }
  }

  loadMoreWords(): Promise<void> | undefined {
    if (!this.state.loading.words && this.state.dictionary) {
      this.updateState({
        ...this.state,
        currentPage: {
          ...this.state.currentPage,
          words: this.state.currentPage.words + 1,
        },
      })
      return this.loadWords()
    }
    return undefined
  }

  loadMorePairs(): Promise<void> | undefined {
    if (!this.state.loading.pairs && this.state.dictionary) {
      this.updateState({
        ...this.state,
        currentPage: {
          ...this.state.currentPage,
          pairs: this.state.currentPage.pairs + 1,
        },
      })
      return this.loadPairs()
    }
    return undefined
  }

  // Update dictionary information (title, description, etc.)
  updateDictionary(dictionary: Dictionary): void {
    this.updateState({
      ...this.state,
      dictionary: dictionary,
    })
  }

  // Add new word to the dictionary
  addWord(word: Word): void {
    // Check if this word belongs to the current dictionary
    if (
      this.state.dictionary &&
      word.dictionaries &&
      word.dictionaries.some(dic => dic.id === this.state.dictionary!.id)
    ) {
      this.updateState({
        ...this.state,
        words: [word, ...this.state.words],
        totalItems: {
          ...this.state.totalItems,
          words: this.state.totalItems.words + 1,
        },
      })

      // Update dictionary's word count
      if (this.state.dictionary) {
        this.updateState({
          ...this.state,
          dictionary: {
            ...this.state.dictionary,
            words_count: (this.state.dictionary.words_count || 0) + 1,
          },
        })
      }
    }
  }

  // Update existing word
  updateWord(word: Word): void {
    const updatedWords = this.state.words.map(w => (w.id === word.id ? word : w))
    this.updateState({
      ...this.state,
      words: updatedWords,
    })
  }

  // Remove word from dictionary
  removeWord(wordId: number): void {
    const filteredWords = this.state.words.filter(w => w.id !== wordId)
    this.updateState({
      ...this.state,
      words: filteredWords,
      totalItems: {
        ...this.state.totalItems,
        words: Math.max(0, this.state.totalItems.words - 1),
      },
    })

    // Update dictionary's word count
    if (this.state.dictionary) {
      this.updateState({
        ...this.state,
        dictionary: {
          ...this.state.dictionary,
          words_count: Math.max(0, (this.state.dictionary.words_count || 0) - 1),
        },
      })
    }
  }

  // Add new pair to the dictionary
  addPair(pair: Pair): void {
    // Check if this pair belongs to the current dictionary
    if (
      this.state.dictionary &&
      pair.dictionaries &&
      pair.dictionaries.some(dic => dic.id === this.state.dictionary!.id)
    ) {
      this.updateState({
        ...this.state,
        pairs: [pair, ...this.state.pairs],
        totalItems: {
          ...this.state.totalItems,
          pairs: this.state.totalItems.pairs + 1,
        },
      })

      // Update dictionary's pair count
      if (this.state.dictionary) {
        this.updateState({
          ...this.state,
          dictionary: {
            ...this.state.dictionary,
            pairs_count: (this.state.dictionary.pairs_count || 0) + 1,
          },
        })
      }
    }
  }

  // Update existing pair
  updatePair(pair: Pair): void {
    const updatedPairs = this.state.pairs.map(p => (p.id === pair.id ? pair : p))
    this.updateState({
      ...this.state,
      pairs: updatedPairs,
    })
  }

  // Remove pair from dictionary
  removePair(pairId: number): void {
    const filteredPairs = this.state.pairs.filter(p => p.id !== pairId)
    this.updateState({
      ...this.state,
      pairs: filteredPairs,
      totalItems: {
        ...this.state.totalItems,
        pairs: Math.max(0, this.state.totalItems.pairs - 1),
      },
    })

    // Update dictionary's pair count
    if (this.state.dictionary) {
      this.updateState({
        ...this.state,
        dictionary: {
          ...this.state.dictionary,
          pairs_count: Math.max(0, (this.state.dictionary.pairs_count || 0) - 1),
        },
      })
    }
  }

  // Clear all state (useful when navigating away from dictionary)
  clearState(): void {
    this.state = {
      dictionary: null,
      words: [],
      pairs: [],
      currentPage: {
        words: 1,
        pairs: 1,
      },
      totalItems: {
        words: 0,
        pairs: 0,
      },
      loading: {
        dictionary: false,
        words: false,
        pairs: false,
      },
      error: {
        dictionary: null,
        words: null,
        pairs: null,
      },
    }
    this.stateSubject.next(this.state)
  }

  private updateState(newState: DictionaryState): void {
    this.state = newState
    this.stateSubject.next(this.state)
  }
}
